import { z } from 'zod';
// S-FIX-5: entity imports для list-response schemas (BE.8.1/8.2).
// Note: aliased _Invoice/_Payment/_Settlement чтобы не конфликтовать с inline
// types ниже. Эти imports НЕ должны быть в середине файла — ломают
// initialization order через circular references (RunSummary TDZ).
import { Invoice as _Invoice } from './entities/invoice.js';
import { Payment as _Payment } from './entities/payment.js';
import { PnlBasis } from './entities/pnl.js';
import { Settlement as _Settlement } from './entities/settlement.js';
import {
  AnomalySeverity,
  AnomalyType,
  BusinessStatus,
  ConflictType,
  ExportFormat,
  ExportStatus,
  IntegrationHealth,
  IntegrationSyncMode,
  JobStatus,
  Role,
  RunBusinessStatus,
  RuntimeStatus,
  SourceSystem,
  StockEntryType,
  TaskType,
  ValuationMethod,
  VarianceReason,
  WoSignalType,
  WoWarningType,
} from './enums.js';
// PeriodCloseBlockerKind НЕ импортируется — circular cycle между api-contracts
// и legacy-ui-contracts ломает evaluation order и вызывает TDZ на RunSummary
// при загрузке backend (инцидент 2026-05-16 v0.3.2/v0.3.3). Inline enum в
// PeriodCloseStateResponse дублирует значения вручную.
import {
  DecimalString,
  IsoDate,
  IsoDateTime,
  MoneyAmount,
  PositiveDecimalString,
  QueryBool,
  UuidString,
} from './primitives.js';

// ─── Runs ─────────────────────────────────────────────────────────────

/** Вход для POST /api/v1/runs. */
export const NewRunInput = z
  .object({
    task_type: TaskType,
    period: z
      .object({
        date_from: IsoDate,
        date_to: IsoDate,
      })
      .strict(),
    scope: z.record(z.unknown()),
    evidence_hint: z.array(z.string()).optional(),
  })
  .strict();
export type NewRunInput = z.infer<typeof NewRunInput>;

// ─── Approvals ────────────────────────────────────────────────────────

/** Одна подпись approver-а. */
export const ApprovalSignature = z
  .object({
    user_id: UuidString,
    role: Role,
    signed_at: IsoDateTime,
    reason: z.string().optional(),
  })
  .strict();
export type ApprovalSignature = z.infer<typeof ApprovalSignature>;

// ─── Run detail tabs (SSOT для frontend) ──────────────────────────────

/** Табы, всегда присутствующие на Run detail. */
export const BASE_RUN_DETAIL_TABS = [
  'source_conflicts',
  'anomalies',
  'recommended_actions',
  'evidence',
  'observability',
] as const;

/** Conditional табы по task_type. */
export const RunDetailTabsMap = {
  inventory_reconcile: [...BASE_RUN_DETAIL_TABS, 'inventory_result'] as const,
  writeoff_review: [...BASE_RUN_DETAIL_TABS, 'writeoff_result'] as const,
  cost_draft: [...BASE_RUN_DETAIL_TABS, 'cost_result'] as const,
  anomaly_investigation: [...BASE_RUN_DETAIL_TABS] as const,
  period_close_draft: [
    ...BASE_RUN_DETAIL_TABS,
    'inventory_result',
    'writeoff_result',
    'cost_result',
  ] as const,
} as const satisfies Record<TaskType, readonly string[]>;

// ─── Integration status ───────────────────────────────────────────────

/**
 * S-FIX-1-BE.A.2 — режим push'а 1С.
 *   - placeholder: LIVE=false; реальный POST не выполняется, возвращается pending-<id>.
 *   - live_shadow: LIVE=true SHADOW=true; реальный POST на mock/staging OData.
 *   - live_prod: LIVE=true SHADOW=false; реальный POST на prod 1С.
 * FE рендерит соответствующий pill через m.integration_*_pill_label().
 */
export const IntegrationPushMode = z.enum(['placeholder', 'live_shadow', 'live_prod']);
export type IntegrationPushMode = z.infer<typeof IntegrationPushMode>;

export const IntegrationStatusItem = z
  .object({
    peer: z.enum(['1c', 'bitrix', 'adesk']),
    health: IntegrationHealth,
    sync_mode: IntegrationSyncMode,
    last_pull_at: IsoDateTime.nullable(),
    last_push_at: IsoDateTime.nullable(),
    last_attempt_at: IsoDateTime.nullable(),
    onboarding_date: IsoDate.nullable(),
    /** B-A.10 — structural code вместо freeform message. FE локализует через m.integration_status_{code}(). */
    status_code: z
      .enum(['ok', 'pending', 'rate_limited', 'auth_error', 'network_error', 'unknown'])
      .nullable()
      .optional(),
    /** Structural params (retry_after, last_error_code) — не freeform текст. */
    params: z.record(z.unknown()).optional(),
    /** S-FIX-1-BE.A.2 — push mode для 1C peer; для других peer-ов опционально. */
    mode: IntegrationPushMode.optional(),
  })
  .strict();
export type IntegrationStatusItem = z.infer<typeof IntegrationStatusItem>;

export const IntegrationStatusResponse = z
  .object({
    items: z.array(IntegrationStatusItem),
  })
  .strict();
export type IntegrationStatusResponse = z.infer<typeof IntegrationStatusResponse>;

// MoneyAmount / Currency — импортируются напрямую из @staiter/shared
// (реэкспортируются барретом index.ts). В api-contracts используются ниже как dependency.

// ─── Generic Job Status ───────────────────────────────────────────────

export const JobType = z.enum([
  'bulk_import',
  'async_export',
  'adapter_refresh',
  'valuation_backfill',
  'auto_expiry',
  'excel_publish',
]);
export type JobType = z.infer<typeof JobType>;

export const JobStatusResponse = z
  .object({
    job_id: UuidString,
    type: JobType,
    status: JobStatus,
    progress: z.number().int().min(0).max(100),
    created_at: IsoDateTime,
    started_at: IsoDateTime.nullable(),
    finished_at: IsoDateTime.nullable(),
    result_url: z.string().url().nullable(),
    error: z
      .object({
        code: z.string(),
        message: z.string(),
      })
      .strict()
      .nullable(),
    meta: z.record(z.unknown()).optional(),
  })
  .strict();
export type JobStatusResponse = z.infer<typeof JobStatusResponse>;

// ─── Export request (unified pattern, 5.6) ───────────────────────────

export const AsyncExportRequest = z
  .object({
    format: ExportFormat,
    scope: z.record(z.unknown()),
  })
  .strict();
export type AsyncExportRequest = z.infer<typeof AsyncExportRequest>;

export const AsyncExportResponse = z
  .object({
    job_id: UuidString,
    status: ExportStatus,
  })
  .strict();
export type AsyncExportResponse = z.infer<typeof AsyncExportResponse>;

// ─── Common response helpers ──────────────────────────────────────────

/**
 * Legacy ApiError — для перехода на `ApiErrorStrict` (entities/api-error.ts).
 *
 * Backend envelope — structural (R-BE.5): `{code, trace_id, details}`; `message` НЕ возвращается.
 * FE локализует через `renderApiError(code, details)` из `@staiter/i18n`.
 * `message` оставлен `.optional()` для backward-compat старых endpoint-ов.
 */
export const ApiError = z
  .object({
    code: z.string(),
    message: z.string().optional(),
    retry_after: z.number().int().optional(),
    details: z.record(z.unknown()).optional(),
    /** B10 — trace_id в body envelope (дубль X-Trace-Id header). */
    trace_id: z.string().optional(),
  })
  .passthrough();
export type ApiError = z.infer<typeof ApiError>;

// ─── Run list / detail summary ────────────────────────────────────────

export const RunSummary = z.object({
  id: UuidString,
  entity_id: UuidString,
  task_type: TaskType,
  runtime_status: RuntimeStatus,
  // BE-1: RunBusinessStatus = canonical BusinessStatus ∪ lifecycle states.
  business_status: RunBusinessStatus.nullable().optional(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
  started_at: IsoDateTime.optional(),
  finished_at: IsoDateTime.nullable().optional(),
  created_by: UuidString.optional(),
  period: z
    .object({
      date_from: IsoDate,
      date_to: IsoDate,
    })
    .strict(),
  // S-FIX-3 hotfix-3: backend list handler doesn't compute these — make optional.
  // S-FIX-3 commit 7f84944: BE handler now does real COALESCE batch join.
  conflicts_count: z.number().int().nonnegative().default(0),
  anomalies_count: z.number().int().nonnegative().default(0),
});
export type RunSummary = z.infer<typeof RunSummary>;

// ─── Shared filters ───────────────────────────────────────────────────

export const ConflictFilter = z
  .object({
    conflict_type: ConflictType.optional(),
    resolved: z.boolean().optional(),
  })
  .strict();
export type ConflictFilter = z.infer<typeof ConflictFilter>;

export const AnomalyFilter = z
  .object({
    anomaly_type: AnomalyType.optional(),
    severity: AnomalySeverity.optional(),
  })
  .strict();
export type AnomalyFilter = z.infer<typeof AnomalyFilter>;

// ─── Stock balance query (example of MoneyAmount usage) ──────────────

// ─── Cursor pagination envelope ────────────────────────────────────────

export const PaginationQuery = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  })
  .strict();
export type PaginationQuery = z.infer<typeof PaginationQuery>;

export const PaginatedEnvelope = <T extends z.ZodTypeAny>(item: T) =>
  z
    .object({
      items: z.array(item),
      next_cursor: z.string().nullable().optional(),
    })
    .strict();

// ─── Runs list / detail ────────────────────────────────────────────────

export const RunListQuery = z
  .object({
    status: RuntimeStatus.optional(),
    task_type: TaskType.optional(),
    period_from: IsoDate.optional(),
    period_to: IsoDate.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
  })
  .strict();
export type RunListQuery = z.infer<typeof RunListQuery>;

export const ApprovalActionBody = z
  .object({
    reason: z.string().max(1000).optional(),
    approver_role: Role.optional(),
  })
  .strict();
export type ApprovalActionBody = z.infer<typeof ApprovalActionBody>;

// ─── Conflicts ──────────────────────────────────────────────────────────

export const ConflictResolveBody = z
  .object({
    action: z.enum(['accept_ours', 'accept_theirs', 'merge', 'escalate']),
    reason: z.string().min(1).max(2000),
  })
  .strict();
export type ConflictResolveBody = z.infer<typeof ConflictResolveBody>;

// ─── Anomalies ──────────────────────────────────────────────────────────

export const AnomalyListQuery = z
  .object({
    severity: AnomalySeverity.optional(),
    type: AnomalyType.optional(),
    status: z.enum(['open', 'reviewed', 'escalated', 'dismissed']).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
  })
  .strict();
export type AnomalyListQuery = z.infer<typeof AnomalyListQuery>;

export const AnomalyActionBody = z
  .object({
    reason: z.string().max(2000).optional(),
    escalate_to: UuidString.optional(),
  })
  .strict();
export type AnomalyActionBody = z.infer<typeof AnomalyActionBody>;

export const AnomalyBulkActionBody = z
  .object({
    ids: z.array(UuidString).min(1).max(200),
    action: z.enum(['review', 'escalate', 'dismiss']),
    reason: z.string().max(2000).optional(),
    escalate_to: UuidString.optional(),
  })
  .strict();
export type AnomalyBulkActionBody = z.infer<typeof AnomalyBulkActionBody>;

// ─── Evidence ───────────────────────────────────────────────────────────

export const EvidenceListQuery = z
  .object({
    source: z.string().optional(),
    period_from: IsoDate.optional(),
    period_to: IsoDate.optional(),
    document_id: z.string().optional(),
    hash: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  })
  .strict();
export type EvidenceListQuery = z.infer<typeof EvidenceListQuery>;

// ─── Run activity log ───────────────────────────────────────────────────

export const RunActivityEvent = z
  .object({
    id: UuidString,
    run_id: UuidString,
    occurred_at: IsoDateTime,
    event_type: z.string(),
    payload: z.record(z.unknown()),
    trace_id: z.string().nullable(),
    seq: z.number().int().nonnegative(),
  })
  .strict();
export type RunActivityEvent = z.infer<typeof RunActivityEvent>;

export const StockBalanceRow = z
  .object({
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable(),
    balance_qty: z.string().regex(/^-?\d+(\.\d+)?$/),
    balance_value: MoneyAmount,
    valuation_rate: MoneyAmount,
    as_of: IsoDateTime,
  })
  .strict();
export type StockBalanceRow = z.infer<typeof StockBalanceRow>;

// ─── Inventory master CRUD ──────────────────────────────────────────────

export const ItemUpsertInput = z
  .object({
    sku: z.string().min(1).max(100),
    name: z.string().min(1).max(400),
    item_type: z.enum(['raw_material', 'sub_assembly', 'finished_good', 'consumable', 'service']),
    item_group_id: UuidString.nullable().optional(),
    default_uom: z.string().min(1),
    stock_uom: z.string().min(1),
    has_variants: z.boolean().optional(),
    has_batch_no: z.boolean().optional(),
    has_serial_no: z.boolean().optional(),
    is_stock_item: z.boolean().optional(),
    is_purchase_item: z.boolean().optional(),
    is_sales_item: z.boolean().optional(),
    description: z.string().nullable().optional(),
  })
  .strict();
export type ItemUpsertInput = z.infer<typeof ItemUpsertInput>;

export const ItemListQuery = z
  .object({
    q: z.string().optional(),
    item_type: z.string().optional(),
    item_group_id: UuidString.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
  })
  .strict();
export type ItemListQuery = z.infer<typeof ItemListQuery>;

export const WarehouseUpsertInput = z
  .object({
    code: z.string().min(1).max(100),
    name: z.string().min(1).max(400),
    parent_id: UuidString.nullable().optional(),
    is_group: z.boolean().optional(),
    is_active: z.boolean().optional(),
    address: z.string().nullable().optional(),
  })
  .strict();
export type WarehouseUpsertInput = z.infer<typeof WarehouseUpsertInput>;

export const BatchUpsertInput = z
  .object({
    batch_no: z.string().min(1),
    item_id: UuidString,
    mfg_date: IsoDate.nullable().optional(),
    expiry_date: IsoDate.nullable().optional(),
    qc_status: z.enum(['pending', 'passed', 'failed', 'quarantine']).optional(),
  })
  .strict();
export type BatchUpsertInput = z.infer<typeof BatchUpsertInput>;

export const BatchListQuery = z
  .object({
    item_id: UuidString.optional(),
    qc_status: z.string().optional(),
    expiring_before: IsoDate.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
  })
  .strict();
export type BatchListQuery = z.infer<typeof BatchListQuery>;

export const BomUpsertInput = z
  .object({
    bom_template_key: z.string().min(1),
    version: z.number().int().min(1).optional(),
    name: z.string().min(1),
    item_id: UuidString,
    output_qty: z.string().regex(/^-?\d+(\.\d+)?$/),
    uom: z.string().min(1),
    is_active: z.boolean().optional(),
    is_default: z.boolean().optional(),
  })
  .strict();
export type BomUpsertInput = z.infer<typeof BomUpsertInput>;

export const BomExplodeInput = z
  .object({
    qty: z.string().regex(/^-?\d+(\.\d+)?$/),
  })
  .strict();
export type BomExplodeInput = z.infer<typeof BomExplodeInput>;

// ─── BOM nested create + sub-resource schemas ──────────────────────────

export const BomItemInput = z
  .object({
    item_id: UuidString,
    qty: z.string().regex(/^-?\d+(\.\d+)?$/),
    uom: z.string().min(1),
    position: z.number().int().nonnegative(),
    scrap_pct: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/)
      .optional(),
    is_alternate: z.boolean().default(false),
    alternate_of_item_id: UuidString.optional(),
  })
  .strict();
export type BomItemInput = z.infer<typeof BomItemInput>;

export const BomItemSchema = BomItemInput.extend({
  id: UuidString,
  bom_id: UuidString,
}).strict();
export type BomItemSchema = z.infer<typeof BomItemSchema>;

export const BomOperationInput = z
  .object({
    operation_name: z.string().min(1),
    time_minutes: z.number().nonnegative(),
    position: z.number().int().nonnegative(),
    workstation: z.string().optional(),
    cost_per_minute: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/)
      .optional(),
  })
  .strict();
export type BomOperationInput = z.infer<typeof BomOperationInput>;

export const BomOperationSchema = BomOperationInput.extend({
  id: UuidString,
  bom_id: UuidString,
}).strict();
export type BomOperationSchema = z.infer<typeof BomOperationSchema>;

// Вложенные массивы объявлены здесь, а не читаются хендлером из сырого тела:
// `zValidator` уже вычитал body, повторный `c.req.raw.clone()` падал TypeError
// (Request.clone: unusable) — POST /boms отдавал 500 при header-only теле и 422
// при теле с items (strict-схема без этих ключей). Оба необязательны: мастер шлёт
// их всегда, API-клиент может создать пустую спецификацию и наполнить её
// через POST /boms/:id/items.
export const BomCreateInput = BomUpsertInput.extend({
  items: z.array(BomItemInput).optional(),
  operations: z.array(BomOperationInput).optional(),
});
export type BomCreateInput = z.infer<typeof BomCreateInput>;

// ─── Usage check responses ─────────────────────────────────────────────

export const ItemUsageResponse = z
  .object({
    bom_count: z.number().int().nonnegative(),
    batch_count: z.number().int().nonnegative(),
    stock_entry_count: z.number().int().nonnegative(),
    sle_count: z.number().int().nonnegative(),
    can_delete: z.boolean(),
  })
  .strict();
export type ItemUsageResponse = z.infer<typeof ItemUsageResponse>;

export const BatchUsageResponse = z
  .object({
    sle_count: z.number().int().nonnegative(),
    stock_entry_count: z.number().int().nonnegative(),
    can_delete: z.boolean(),
  })
  .strict();
export type BatchUsageResponse = z.infer<typeof BatchUsageResponse>;

export const BomUsageResponse = z
  .object({
    work_order_count: z.number().int().nonnegative(),
    can_delete: z.boolean(),
  })
  .strict();
export type BomUsageResponse = z.infer<typeof BomUsageResponse>;

export const WarehouseUsageResponse = z
  .object({
    stock_entry_count: z.number().int().nonnegative(),
    sle_count: z.number().int().nonnegative(),
    child_count: z.number().int().nonnegative(),
    can_delete: z.boolean(),
  })
  .strict();
export type WarehouseUsageResponse = z.infer<typeof WarehouseUsageResponse>;

// ─── Stock balance / ledger queries ─────────────────────────────────────

export const StockBalanceQuery = z
  .object({
    item: UuidString.optional(),
    warehouse: UuidString.optional(),
    batch: UuidString.optional(),
    as_of: IsoDateTime.optional(),
    /**
     * TCA-F2 — только строки без оценки (ставка пустая или ноль). Отбор на сервере:
     * на клиенте метка фильтровала загруженную страницу (100 строк), а плитка считала
     * всю выборку — пустота «по отбору ничего не найдено» могла стоять под «Строк без
     * оценки: N». Сводка (`summary`) считается по выборке ДО этого отбора.
     */
    unvalued: QueryBool.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
  })
  .strict();
export type StockBalanceQuery = z.infer<typeof StockBalanceQuery>;

export const StockLedgerQuery = z
  .object({
    item: UuidString.optional(),
    warehouse: UuidString.optional(),
    batch: UuidString.optional(),
    posting_from: IsoDateTime.optional(),
    posting_to: IsoDateTime.optional(),
    voucher_id: UuidString.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
  })
  .strict();
export type StockLedgerQuery = z.infer<typeof StockLedgerQuery>;

/**
 * B48 пака `tails-closeout` (NC-39) — ответ предпросчёта плановой стоимости проекта
 * (`POST /task-module/projects/:id/baseline-preview`). `revenue` — операнд маржи:
 * выставленное по проекту из AR (`ar`), ноль без счетов (`none`) или сумма из формы
 * (`form`). Без него «маржа станет −X» — число без провенанса.
 */
export const ProjectBaselinePreviewResponse = z
  .object({
    project_id: UuidString,
    old_margin: z.string(),
    new_margin: z.string(),
    delta: z.string(),
    variance_pct: z.string(),
    variance_comparable: z.boolean(),
    tier2_bases: z.array(z.enum(['relative', 'first_baseline', 'material', 'method'])),
    requires_tier2: z.boolean(),
    revenue: MoneyAmount.extend({ source: z.enum(['ar', 'none', 'form']) }).strict(),
  })
  .strict();
export type ProjectBaselinePreviewResponse = z.infer<typeof ProjectBaselinePreviewResponse>;

/**
 * B37 пака `tails-closeout` (WHS-F29) — фильтры реестра складских документов.
 *
 * До правки `GET /stock-entries` понимал только `status` и `project_id`: найти приход
 * за неделю или всё, что проходило через склад, можно было только глазами. Схема
 * строгая — неизвестный параметр отбивается 422, а не игнорируется (плацебо-фильтр
 * хуже отсутствия).
 *
 * - `warehouse_id` — ЛЮБАЯ нога: склад шапки (откуда/куда) или склад любой строки;
 * - `from`/`to` — даты включительно; проведённые и отменённые — по `posting_datetime`,
 *   черновики — по `created_at` (у черновика дата проводки — намерение, а не факт);
 * - `status` — строкой: неизвестное значение отбивает хендлер своим кодом
 *   `unknown_status_filter` (контракт B4 [П-13]).
 */
export const StockEntryListQuery = z
  .object({
    type: StockEntryType.optional(),
    warehouse_id: UuidString.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    status: z.string().optional(),
    project_id: UuidString.optional(),
    cursor: z.string().optional(),
    limit: z.string().optional(),
  })
  .strict();
export type StockEntryListQuery = z.infer<typeof StockEntryListQuery>;

// ─── Stock Entry draft (5.3) ────────────────────────────────────────────

export const StockEntryDraftItem = z
  .object({
    position: z.number().int().nonnegative().optional(),
    item_id: UuidString,
    from_warehouse_id: UuidString.nullable().optional(),
    to_warehouse_id: UuidString.nullable().optional(),
    batch_id: UuidString.nullable().optional(),
    qty: z.string().regex(/^-?\d+(\.\d+)?$/),
    uom: z.string().min(1),
    conversion_factor: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/)
      .optional(),
    /**
     * B3 (пак stock-entry-wizard) — цена строки СТАЛА необязательной [П-4].
     *
     * Была обязательной, а мастер её не спрашивал вовсе — то есть путь создания
     * упирался в поле, которого нет в форме. Обратное решение («спросить всегда»)
     * не годится: у списания и перемещения честной цены у оператора НЕТ, она
     * берётся из оценки склада. Поэтому обязательность теперь зависит от типа
     * документа и проверяется на сервере, где эта оценка доступна:
     * приход (`material_receipt`) без цены → 422 на поле, остальные типы —
     * последняя оценка item×warehouse, а при её отсутствии 0 (см. §6 решения).
     */
    basic_rate: MoneyAmount.optional(),
  })
  .strict();

export const StockEntryDraftInput = z
  .object({
    /**
     * B1 — номер СТАЛ необязательным [П-8]. Мастер его не спрашивал, а схема
     * требовала: 422 на каждой попытке создать документ из интерфейса. Пустой
     * (или legacy-sentinel `AUTO`) означает «сгенерировать серверу»,
     * присланный — сохраняется как есть.
     */
    voucher_no: z.string().min(1).nullable().optional(),
    stock_entry_type: z.enum([
      'material_receipt',
      'material_issue',
      'material_transfer',
      'manufacture',
      'repack',
      'material_transfer_for_manufacture',
    ]),
    posting_datetime: IsoDateTime,
    from_warehouse_id: UuidString.nullable().optional(),
    to_warehouse_id: UuidString.nullable().optional(),
    work_order_id: UuidString.nullable().optional(),
    bom_id: UuidString.nullable().optional(),
    /**
     * B1 [П-5] — поставщик и место затрат. Мастер спрашивал их шага назад и
     * ВЫБРАСЫВАЛ на границе: в теле полей не было, схема `.strict()` их бы и не
     * пропустила. Введённое пользователем обязано доезжать до базы.
     *
     * ОТСТУПЛЕНИЕ ОТ §3.1 РЕШЕНИЯ — осознанное, с проверкой об код. Решение
     * называло оба поля `UuidString`; для поставщика это верно (есть таблица
     * `counterparties`, и в форме стоит пикер, отдающий UUID), а для места
     * затрат — нет: справочника мест затрат в продукте НЕ СУЩЕСТВУЕТ ни одной
     * таблицей, поле в мастере — обычный текстовый ввод, и его собственная
     * подсказка гласит «Место затрат / заказ / проект». UUID-тип превратил бы
     * работающее на вид поле в гарантированный 422 на всём, что туда вводят.
     * Взята уже принятая в проекте форма: `cost_center` — свободная строка ≤64
     * (так же объявлено в трёх контрактах финансового контура).
     */
    supplier_id: UuidString.nullable().optional(),
    cost_center: z.string().min(1).max(64).nullable().optional(),
    remarks: z.string().nullable().optional(),
    items: z.array(StockEntryDraftItem).min(1),
  })
  .strict();
export type StockEntryDraftInput = z.infer<typeof StockEntryDraftInput>;

/**
 * B1 [П-2] — ответ создания складского документа.
 *
 * Ручка отдаёт `{header, items}` строками Drizzle (camelCase), а фронт парсил
 * ответ схемой `StockEntryDetail` — плоской, snake_case, с полем `lines`. Ни
 * одно поле не совпадало, поэтому даже успешное создание падало разбором ответа.
 * Схема описывает ФАКТИЧЕСКУЮ форму ручки; `StockEntryDetail` остаётся там, где
 * отвечает detail-ручка.
 *
 * `.passthrough()` осознан: строка таблицы несёт служебные колонки
 * (`sourceSystem`, `fetchedAt`, аудитные метки), которые контракту создания
 * не нужны, но и срезать их незачем — потребитель берёт то, что объявлено.
 */
export const StockEntryCreateResultHeader = z
  .object({
    id: UuidString,
    entityId: UuidString,
    voucherNo: z.string(),
    stockEntryType: z.string(),
    postingDatetime: z.string(),
    fromWarehouseId: UuidString.nullable(),
    toWarehouseId: UuidString.nullable(),
    workOrderId: UuidString.nullable(),
    bomId: UuidString.nullable(),
    supplierId: UuidString.nullable().optional(),
    costCenter: z.string().nullable().optional(),
    remarks: z.string().nullable(),
    isSubmitted: z.boolean(),
    isCancelled: z.boolean(),
  })
  .passthrough();

export const StockEntryCreateResultItem = z
  .object({
    id: UuidString,
    stockEntryId: UuidString,
    position: z.number().int(),
    itemId: UuidString,
    fromWarehouseId: UuidString.nullable(),
    toWarehouseId: UuidString.nullable(),
    batchId: UuidString.nullable(),
    qty: DecimalString,
    uom: z.string(),
    conversionFactor: DecimalString,
    stockQty: DecimalString,
    basicRate: DecimalString,
    basicRateCurrency: z.string(),
    basicAmount: DecimalString,
    basicAmountCurrency: z.string(),
    /**
     * B3 §6 — строка проведена по нулевой оценке: цены не было ни в запросе,
     * ни в истории склада. Сумма документа при этом честно мала, а не соврана
     * типом; валюационный пак пересчитает.
     */
    rateFallbackApplied: z.boolean().optional(),
  })
  .passthrough();

export const StockEntryCreateResult = z
  .object({
    header: StockEntryCreateResultHeader,
    items: z.array(StockEntryCreateResultItem),
  })
  .passthrough();
export type StockEntryCreateResult = z.infer<typeof StockEntryCreateResult>;

// ─── Work Orders list (read-only stub) ──────────────────────────────────

/**
 * W19.1 — явная семантика date range:
 *   - `planned_start_from/to` — фильтр по `plannedStart`
 *   - `actual_start_from/to` — фильтр по `actualStart`
 * `period_from/to` — deprecated alias на `planned_start_from/to` (удалить в next релизе).
 *
 * W19.3 — `sort` enum (default — `updated_desc`).
 */
export const WorkOrderListQuery = z
  .object({
    status: z.string().optional(),
    item_id: UuidString.optional(),
    executor_id: UuidString.optional(),
    /** @deprecated W19.1 — use `planned_start_from`. */
    period_from: IsoDate.optional(),
    /** @deprecated W19.1 — use `planned_start_to`. */
    period_to: IsoDate.optional(),
    planned_start_from: IsoDate.optional(),
    planned_start_to: IsoDate.optional(),
    actual_start_from: IsoDate.optional(),
    actual_start_to: IsoDate.optional(),
    sort: z
      .enum(['updated_desc', 'updated_asc', 'planned_start_asc', 'planned_start_desc'])
      .optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
    /** P9.8 — фильтр для FE Project linkage panel (G11). */
    project_id: UuidString.optional(),
    project_task_id: UuidString.optional(),
  })
  .strict();
export type WorkOrderListQuery = z.infer<typeof WorkOrderListQuery>;

// ─── Exports registry (5.6) ─────────────────────────────────────────────

export const ExportRow = z
  .object({
    id: UuidString,
    resource: z.string(),
    format: ExportFormat,
    status: ExportStatus,
    job_id: z.string().nullable(),
    file_url: z.string().nullable(),
    file_size_bytes: z.number().int().nonnegative().nullable(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
    created_at: IsoDateTime,
    started_at: IsoDateTime.nullable(),
    completed_at: IsoDateTime.nullable(),
    expires_at: IsoDateTime,
  })
  .strict();
export type ExportRow = z.infer<typeof ExportRow>;

export const ExportListQuery = z
  .object({
    status: ExportStatus.optional(),
    resource: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  })
  .strict();
export type ExportListQuery = z.infer<typeof ExportListQuery>;

export const ExportCreateInput = z
  .object({
    resource: z.string().min(1),
    format: ExportFormat,
    params: z.record(z.unknown()).optional(),
    force_async: z.boolean().optional(),
  })
  .strict();
export type ExportCreateInput = z.infer<typeof ExportCreateInput>;

// ─── Audit list ─────────────────────────────────────────────────────────

// ─── S6 Workspaces ─────────────────────────────────────────────────────

export const ReconciliationWorkspaceQuery = z
  .object({
    period: z.string().regex(/^\d{4}-\d{2}$/, 'period must be YYYY-MM'),
  })
  .strict();
export type ReconciliationWorkspaceQuery = z.infer<typeof ReconciliationWorkspaceQuery>;

/** UI-flat row для pending документа сверки. */
export const PendingReconciliationDoc = z.object({
  id: UuidString,
  warehouse: UuidString.nullable().optional(),
  warehouse_label: z.string().optional(),
  type: z.string().optional(),
  created_by: UuidString.nullable().optional(),
  lines_count: z.number().int().nonnegative(),
  variance_value: z.string().nullable().optional(),
  status: z.string().optional(),
});
export type PendingReconciliationDoc = z.infer<typeof PendingReconciliationDoc>;

export const ReconciliationWorkspaceResponse = z.object({
  period: z.string(),
  last_run: z
    .object({
      run_id: UuidString,
      status: RuntimeStatus,
      finished_at: IsoDateTime.nullable(),
      /** UI-alias */
      business_status: BusinessStatus.optional(),
      agent_draft: z.record(z.unknown()).nullable().optional(),
    })
    .nullable(),
  agent_draft: z.record(z.unknown()).nullable(),
  pending_count_documents: z.array(
    z
      .object({
        id: UuidString,
        warehouse: UuidString.nullable(),
        type: z.string(),
        created_by: UuidString.nullable(),
        lines_count: z.number().int().nonnegative(),
      })
      .strict(),
  ),
  /** UI-alias для pending_count_documents с дополнительными полями. */
  pending_documents: z.array(PendingReconciliationDoc).optional(),
  stock_balance_summary: z
    .object({
      items: z.number().int().nonnegative(),
      warehouses: z.number().int().nonnegative(),
      total_value: MoneyAmount,
    })
    .strict(),
  /** UI-alias для stock_balance_summary с дополнительными полями. */
  balance_summary: z
    .object({
      total_items: z.number().int().nonnegative(),
      total_value: MoneyAmount,
      variance_lines: z.number().int().nonnegative(),
    })
    .partial()
    .optional(),
});
export type ReconciliationWorkspaceResponse = z.infer<typeof ReconciliationWorkspaceResponse>;

/** UI-flat row для Writeoff таблицы. */
export const WriteoffRow = z.object({
  item_id: UuidString,
  item_label: z.string(),
  uom: z.string(),
  required_qty: DecimalString,
  allocated_qty: DecimalString,
  variance_qty: DecimalString,
  variance_reason: z.string().nullable(),
  source_batch: UuidString.nullable(),
  source_system: SourceSystem,
});
export type WriteoffRow = z.infer<typeof WriteoffRow>;

export const WriteoffWorkspaceResponse = z.object({
  work_order_id: UuidString,
  required: z.array(
    z
      .object({
        item: UuidString,
        qty: DecimalString,
      })
      .strict(),
  ),
  allocated: z.array(
    z
      .object({
        item: UuidString,
        qty: DecimalString,
        from_batch: UuidString.nullable(),
        value: MoneyAmount,
      })
      .strict(),
  ),
  variance: z.array(
    z
      .object({
        item: UuidString,
        expected_qty: DecimalString,
        actual_qty: DecimalString,
        reason: z.string().nullable().optional(),
      })
      .strict(),
  ),
  /** UI-aliases для удобного рендера таблицы. */
  rows: z.array(WriteoffRow).optional(),
  work_order_label: z.string().optional(),
  status: z.string().optional(),
  total_variance_value: z.string().optional(),
});
export type WriteoffWorkspaceResponse = z.infer<typeof WriteoffWorkspaceResponse>;

export const CostScopeBreakdownItem = z
  .object({
    scope_id: z.string(),
    qty: DecimalString,
    material: MoneyAmount,
    labor: MoneyAmount,
    overhead: MoneyAmount,
    total: MoneyAmount,
  })
  .strict();

/** Одна строка cost breakdown — алиас для UI (материал/labor/оверхед items). */
export const CostBreakdownRow = z.object({
  id: UuidString,
  label: z.string(),
  category: z.string(),
  qty: DecimalString.nullable().optional(),
  uom: z.string().nullable().optional(),
  amount: MoneyAmount,
});
export type CostBreakdownRow = z.infer<typeof CostBreakdownRow>;

export const CostWorkspaceResponse = z.object({
  scope: z
    .object({
      type: z.enum(['period', 'sku', 'wo']),
      id: z.string(),
    })
    .strict(),
  layers: z
    .object({
      material: MoneyAmount,
      labor: MoneyAmount,
      overhead: MoneyAmount,
    })
    .strict(),
  /** UI-flat aliases для layers. */
  material: MoneyAmount.optional(),
  labor: MoneyAmount.optional(),
  overhead: MoneyAmount.optional(),
  total: MoneyAmount,
  qty: DecimalString,
  per_unit_cost: MoneyAmount,
  breakdown: z.array(CostScopeBreakdownItem),
  /** UI-алиас для breakdown, отформатированный в плоские rows. */
  rows: z.array(CostBreakdownRow).optional(),
  evidence_refs: z.array(
    z.union([
      UuidString,
      z
        .object({
          id: UuidString,
          source: z.string().optional(),
          document_id: z.string().nullable().optional(),
          fetched_at: IsoDateTime.optional(),
        })
        .passthrough(),
    ]),
  ),
});
export type CostWorkspaceResponse = z.infer<typeof CostWorkspaceResponse>;

// ─── S6 Approvals queue ────────────────────────────────────────────────

export const ApprovalQueueListQuery = z
  .object({
    // S-FIX-5 fix (2026-05-16): FE отправляет status=pending по default,
    // legacy ApprovalStatus в shared имеет {pending, approved, rejected, expired, waiting}.
    // BE-side query enum выровнен с FE — добавлены 'pending' (alias waiting) и 'expired'.
    // B3 — `withdrawn` и `history` добавлены вместе с очередью намерений: отозванные
    // и истёкшие заявки обязаны быть ВИДНЫ, а не пропадать. Пропавшая заявка
    // неотличима от несозданной, и разбирать «а я точно отправлял?» не по чему.
    status: z
      .enum([
        'waiting',
        'pending',
        'executing',
        'approved',
        'rejected',
        'cancelled',
        'expired',
        'withdrawn',
        'executed_unconfirmed',
        'history',
      ])
      .optional(),
    /**
     * F3 — «мне на решение» против «мои отправленные»: два разных намерения на одном
     * экране. Без фильтра список сливает их в кучу, и адресность, ради которой пак
     * затевался, на экране не видна.
     */
    role: z.enum(['addressee', 'requester']).optional(),
    task_type: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    // BE-5: admin override to surface [E2E-…] test approvals для debugging.
    include_test: QueryBool.optional(),
  })
  .strict();
export type ApprovalQueueListQuery = z.infer<typeof ApprovalQueueListQuery>;

export const ApprovalQueueRow = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    task_type: z.string(),
    // S-FIX-2-FE.3.3: coerce — backend историчски иногда отдавал `tier: '2'` строкой.
    // Coerce-friendly Zod не reject'ит, FE рендерит {row.tier} как number.
    tier: z.coerce.number().int().min(0).max(3),
    subject_kind: z.string(),
    subject_id: z.string(),
    workflow_id: z.string().nullable(),
    title: z.string(),
    details: z.record(z.unknown()),
    // B3 (tier2-approval-requests §3) — очередь стала очередью НАМЕРЕНИЙ, и у
    // намерения есть исходы, которых у записи о решении быть не могло: истекло,
    // отозвано инициатором, исполняется прямо сейчас.
    status: z.enum([
      'waiting',
      'executing',
      'approved',
      'rejected',
      'cancelled',
      'expired',
      'withdrawn',
      'executed_unconfirmed',
    ]),
    requested_by_user_id: UuidString.nullable(),
    // B5 — КОМУ адресована. `null` у строк period-close: они ходят своим воркфлоу.
    addressee_user_id: UuidString.nullable().optional(),
    decided_by_user_id: UuidString.nullable(),
    decided_at: IsoDateTime.nullable(),
    decision_reason: z.string().nullable(),
    /** Срок жизни заявки. Истекшая не исполняется даже при попытке approve. */
    expires_at: IsoDateTime.nullable().optional(),
    /** Тело намерения: что именно произойдёт при согласовании (F4). */
    payload: z.record(z.unknown()).optional(),
    created_at: IsoDateTime,
    age_h: z.number().nonnegative(),
    sla_flag: z.enum(['ok', 'yellow', 'red']),
    /**
     * B40 (T2A-F16, В-7): ждёт дольше нормы — три рабочих дня (`sla.v1.json`
     * `approval_requests`). Только для ждущей заявки; решённая нормы не нарушает.
     */
    overdue_norm: z.boolean().optional(),
    // BE-4 follow-up: required_signatures + collected_signatures denorm в list response —
    // FE Tier 3 banner отображает «Собрано N/M». На list collected обычно пуст для waiting,
    // но field обязательно present чтобы FE schema не fallback'нул в 0.
    required_signatures: z.number().int().min(1).max(3).optional(),
    collected_signatures: z
      .array(
        z.object({
          approver_id: z.string(),
          approver_name: z.string().nullable().optional(),
          role: z.string(),
          signed_at: z.string(),
          note: z.string().optional(),
        }),
      )
      .optional(),
  })
  .strict();
export type ApprovalQueueRow = z.infer<typeof ApprovalQueueRow>;

export const ApprovalDecisionBody = z
  .object({
    signature: ApprovalSignature,
    reason: z.string().max(2000).optional(),
  })
  .strict();
export type ApprovalDecisionBody = z.infer<typeof ApprovalDecisionBody>;

// ─── S6 Stock Entry submit/cancel + Reconciliation submit ─────────────

export const StockEntrySubmitBody = z
  .object({
    signature: ApprovalSignature,
    remarks: z.string().max(1000).optional(),
  })
  .strict();
export type StockEntrySubmitBody = z.infer<typeof StockEntrySubmitBody>;

/**
 * WHS-B14 (акт приёмки 23.09, доработка С-D, П.9; ответ владельца В-34 (3) «по рекомендации») —
 * отмена проведённого складского документа и инвентаризации требует согласующего.
 *
 * Проведение — самоподпись оператора (решение В-23), отмена — подпись исполнителя плюс
 * названный согласующий: не сам исполнитель, с ролью из строки матрицы `stock_entry_cancel`
 * (проверяет сервер, `verifyTier2Approver`). До правки контракт отмены согласующего не
 * принимал вовсе, а диалог выбирал его и выбрасывал на границе.
 */
export const StockDocCancelBody = z
  .object({
    signature: ApprovalSignature,
    approver_user_id: UuidString,
    remarks: z.string().max(1000).optional(),
  })
  .strict();
export type StockDocCancelBody = z.infer<typeof StockDocCancelBody>;

/**
 * WHS-B9, доработка С-E (Р.2) — пикер согласующих отмены берёт компанию ДОКУМЕНТА, как и серверная
 * проверка отмены. Без документа — компания читателя (прежнее поведение). В режиме «Все компании»
 * активной компании нет, и без документа пикер предлагал людей домашней компании, а сервер
 * отказывал им `APPROVER_NOT_IN_ENTITY`.
 */
export const StockCancelDocumentType = z.enum(['stock_entry', 'stock_reconciliation']);
export type StockCancelDocumentType = z.infer<typeof StockCancelDocumentType>;

export const StockCancelApproversQuery = z
  .object({
    q: z.string().trim().min(1).max(200).optional(),
    document_type: StockCancelDocumentType.optional(),
    document_id: UuidString.optional(),
  })
  .strict()
  .refine((v) => (v.document_type === undefined) === (v.document_id === undefined), {
    message: 'document_type и document_id — вместе',
    path: ['document_id'],
  });
export type StockCancelApproversQuery = z.infer<typeof StockCancelApproversQuery>;

/** B59 (WHS-B9) — кандидаты в согласующие отмены: по строке матрицы `stock_entry_cancel`. */
export const StockCancelApproversResponse = z
  .object({
    items: z.array(
      z
        .object({
          id: UuidString,
          email: z.string(),
          full_name: z.string().nullable(),
          role: z.string(),
        })
        .strict(),
    ),
    required_roles: z.array(z.string()),
  })
  .strict();
export type StockCancelApproversResponse = z.infer<typeof StockCancelApproversResponse>;

/**
 * F110 пака `tails-closeout` (С-I, TCA-B9) — кандидаты в согласующие сигнала заказа уровня 2
 * (отмена, выпуск с переопределением): члены компании ЗАКАЗА с ролью, которой матрица прав даёт
 * `work_order:approve`. Та же форма, что у склада. Раньше диалог брал общий список пользователей:
 * в нём были наблюдатели и сервисные аккаунты, а роль показывалась глобальная, не роль в компании.
 */
export const WoApproversResponse = StockCancelApproversResponse;
export type WoApproversResponse = StockCancelApproversResponse;

/**
 * F1 (пак stock-entry-wizard) — ответ проведения и отмены складского документа.
 *
 * Обе ручки отвечают `{ok, id, sle_count, status}` (проведение — ещё и
 * `warnings`), а фронт разбирал ответ схемой `StockEntryDetail` — плоской
 * карточкой документа с обязательными `entry_type`, `posting_at`,
 * `lines_count`, `total_value`. Ни одного из них в ответе нет: разбор падал
 * ЧЕТЫРЬМЯ ошибками подряд на каждом успешном проведении.
 *
 * Поймано живьём на гейте A: документ проводился (200, движения записаны), а
 * интерфейс сообщал об ошибке. Это третий слой той же болезни, что П-2 —
 * контракт объявлен схемой, которую ручка никогда не отдавала.
 */
export const StockEntrySubmitResult = z
  .object({
    ok: z.boolean(),
    id: UuidString,
    /** Сколько строк регистра записано. Ноль у отмены документа без движений. */
    sle_count: z.number().int(),
    status: z.enum(['submitted', 'cancelled']),
    warnings: z
      .array(
        z.object({
          type: z.string(),
          severity: z.enum(['info', 'warn', 'danger']).default('warn'),
          related_item_id: UuidString.optional(),
          related_batch_id: UuidString.optional(),
        }),
      )
      .default([]),
  })
  .passthrough();
export type StockEntrySubmitResult = z.infer<typeof StockEntrySubmitResult>;

export const StockReconciliationDraftItem = z
  .object({
    position: z.number().int().nonnegative().optional(),
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable().optional(),
    expected_qty: DecimalString,
    counted_qty: DecimalString,
    variance_reason: z
      .enum([
        'theft',
        'damage',
        'system_error',
        'measurement_error',
        'evaporation_shrinkage',
        'other',
      ])
      .nullable()
      .optional(),
    valuation_rate: MoneyAmount,
  })
  .strict();

export const StockReconciliationDraftInput = z
  .object({
    // B22 (WHS-B1): мастер номер не спрашивает — пустой генерирует сервер (`SR-{YYYY}-{NNNNN}`).
    voucher_no: z.string().min(1).optional(),
    reconciliation_type: z.enum(['opening_stock', 'physical_count']),
    posting_datetime: IsoDateTime,
    warehouse_id: UuidString.nullable().optional(),
    remarks: z.string().nullable().optional(),
    items: z.array(StockReconciliationDraftItem).min(1),
  })
  .strict();
export type StockReconciliationDraftInput = z.infer<typeof StockReconciliationDraftInput>;

// ─── S6 Period close ───────────────────────────────────────────────────

export const PeriodCloseStartBody = z
  .object({
    period: z.string().regex(/^\d{4}-\d{2}$/),
  })
  .strict();
export type PeriodCloseStartBody = z.infer<typeof PeriodCloseStartBody>;

export const PeriodCloseLockBody = z
  .object({
    signature: ApprovalSignature,
    notes: z.string().max(2000).optional(),
  })
  .strict();
export type PeriodCloseLockBody = z.infer<typeof PeriodCloseLockBody>;

export const PeriodCloseStateResponse = z
  .object({
    period: z.string(),
    is_locked: z.boolean(),
    locked_at: IsoDateTime.nullable(),
    locked_by_user_id: UuidString.nullable(),
    workflow_id: z.string().nullable(),
    blockers: z.array(
      z
        .object({
          // NB: Inline enum (not `import PeriodCloseBlockerKind from './legacy-ui-contracts'`)
          // — circular dependency между api-contracts и legacy-ui-contracts вызывает TDZ
          // на RunSummary при ESM evaluation (инцидент 2026-05-16 v0.3.2/v0.3.3 deploy).
          // Дубликат поддерживается вручную; consolidation требует разрыва цикла отдельно.
          code: z.enum([
            'open_conflicts',
            'pending_approvals',
            'failed_runs',
            'unsubmitted_stock_entries',
            'unsubmitted_landed_cost',
            'unresolved_master_drift',
            'draft_invoices',
            'draft_payments',
            'draft_settlements',
            'missing_reconciliation',
          ]),
          count: z.number().int().nonnegative(),
        })
        .strict(),
    ),
    can_lock: z.boolean(),
  })
  .strict();
export type PeriodCloseStateResponse = z.infer<typeof PeriodCloseStateResponse>;

// ─── S10 Stock Entry cancel-preview ────────────────────────────────────

/** Одна строка compensating SLE в preview. */
export const CancelPreviewSle = z
  .object({
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable(),
    qty_change: DecimalString,
    valuation_rate: MoneyAmount,
    posting_datetime: IsoDateTime,
  })
  .strict();
export type CancelPreviewSle = z.infer<typeof CancelPreviewSle>;

export const CancelPreviewValuationRecompute = z
  .object({
    sle_id: UuidString,
    item_id: UuidString,
    prev_rate: MoneyAmount,
    new_rate: MoneyAmount,
    delta_value: MoneyAmount,
    voucher_ref: UuidString.nullable(),
  })
  .strict();
export type CancelPreviewValuationRecompute = z.infer<typeof CancelPreviewValuationRecompute>;

export const CancelPreviewAffectedDoc = z
  .object({
    type: z.enum(['stock_entry', 'work_order', 'landed_cost', 'stock_reconciliation']),
    id: UuidString,
    recompute_required: z.boolean(),
  })
  .strict();
export type CancelPreviewAffectedDoc = z.infer<typeof CancelPreviewAffectedDoc>;

/**
 * CancelPreviewWarning — structural-only (B-A.6).
 * FE рендерит через `m.cancel_preview_warning_type_label({type})` + `m.cancel_preview_warning_{type}_body(params)`.
 */
export const CancelPreviewWarning = z
  .object({
    type: z.enum([
      'negative_stock_after_cancel',
      'closed_period',
      'downstream_locked',
      'near_expiry_batch',
      'low_stock_margin',
    ]),
    severity: z.enum(['info', 'warn', 'critical']),
    related_item_id: UuidString.nullable().optional(),
    related_batch_id: UuidString.nullable().optional(),
    params: z.record(z.unknown()).optional(),
  })
  .strict();
export type CancelPreviewWarning = z.infer<typeof CancelPreviewWarning>;

export const CancelPreviewResponse = z
  .object({
    stock_entry_id: UuidString,
    compensating_sle: z.array(CancelPreviewSle),
    valuation_recompute: z.array(CancelPreviewValuationRecompute),
    affected_documents: z.array(CancelPreviewAffectedDoc),
    warnings: z.array(CancelPreviewWarning),
    generated_at: IsoDateTime,
  })
  .strict();
export type CancelPreviewResponse = z.infer<typeof CancelPreviewResponse>;

// ─── S10 Landed Cost Voucher ───────────────────────────────────────────

export const LandedCostChargeInput = z
  .object({
    charge_type: z.enum(['freight', 'customs', 'insurance', 'other']),
    description: z.string().min(1),
    amount: MoneyAmount,
    vendor: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .strict();
export type LandedCostChargeInput = z.infer<typeof LandedCostChargeInput>;

export const LandedCostTargetInput = z
  .object({
    source_stock_entry_id: UuidString,
    source_stock_entry_item_id: UuidString,
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable().optional(),
    qty: DecimalString,
    base_amount: MoneyAmount,
    weight: DecimalString.optional(),
  })
  .strict();
export type LandedCostTargetInput = z.infer<typeof LandedCostTargetInput>;

export const LandedCostCreateInput = z
  .object({
    voucher_no: z.string().min(1),
    posting_datetime: IsoDateTime,
    allocation_method: z.enum(['by_amount', 'by_qty', 'by_weight']),
    charges: z.array(LandedCostChargeInput).min(1),
    targets: z.array(LandedCostTargetInput).min(1),
    remarks: z.string().nullable().optional(),
  })
  .strict();
export type LandedCostCreateInput = z.infer<typeof LandedCostCreateInput>;

export const LandedCostPreviewLine = z
  .object({
    target_id: UuidString,
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable(),
    qty: DecimalString,
    prev_valuation_rate: MoneyAmount,
    allocated_amount: MoneyAmount,
    new_valuation_rate: MoneyAmount,
  })
  .strict();
export type LandedCostPreviewLine = z.infer<typeof LandedCostPreviewLine>;

export const LandedCostPreviewResponse = z
  .object({
    voucher_id: UuidString,
    total_charges: MoneyAmount,
    lines: z.array(LandedCostPreviewLine),
    generated_at: IsoDateTime,
  })
  .strict();
export type LandedCostPreviewResponse = z.infer<typeof LandedCostPreviewResponse>;

// ─── S10 1С Tier 3 push aggregates ─────────────────────────────────────

export const OnecPushAggregatesResponse = z
  .object({
    push_id: UuidString,
    status: z.enum(['pushed', 'pending', 'failed']),
    pushed_count: z.number().int().nonnegative(),
    failed: z.array(z.object({ id: UuidString, reason: z.string() }).strict()),
    idempotent_hit: z.boolean(),
  })
  .strict();
export type OnecPushAggregatesResponse = z.infer<typeof OnecPushAggregatesResponse>;

/**
 * B66 пака `tails-closeout` (Ф6, SOLUTION §9.3 п. 1) — тела действий уровня 3.
 *
 * До Ф6 тела несли подписантов (`signatures` / `approvers`), которых вписывал сам инициатор, —
 * подписи были фиктивными. Теперь тело — ровно вход исполнителя `perform*`: что отправить.
 * Подписи живут отдельно, в заявке (`approval_signatures`, B71), и ставятся людьми из своих
 * учётных записей. Прямые ручки 1–11 отвечают 409 `tier3.request_required`.
 */
export const OnecMasterResource = z.enum(['item', 'warehouse', 'bom', 'counterparty']);
export type OnecMasterResource = z.infer<typeof OnecMasterResource>;

/** #1 — мастер-данные. Без `ids` — все записи ресурса; `ids` — только при одном ресурсе. */
export const Tier3MasterDataPayload = z
  .object({
    resources: z.array(OnecMasterResource).min(1).max(4),
    ids: z.array(UuidString).min(1).max(500).optional(),
  })
  .strict()
  .refine((v) => new Set(v.resources).size === v.resources.length, {
    message: 'resources: без повторов',
    path: ['resources'],
  })
  .refine((v) => v.ids === undefined || v.resources.length === 1, {
    message: 'ids — только при одном ресурсе',
    path: ['ids'],
  });
export type Tier3MasterDataPayload = z.infer<typeof Tier3MasterDataPayload>;

/** #2 — агрегаты. */
export const Tier3AggregatesPayload = z
  .object({
    push_type: z.enum(['sle_aggregates', 'stock_entries', 'landed_cost']),
    source_document_ids: z.array(UuidString).min(1).max(10_000),
    period: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .optional(),
  })
  .strict();
export type Tier3AggregatesPayload = z.infer<typeof Tier3AggregatesPayload>;

/** #3 — пакет документов. */
export const Tier3DocumentsBatchPayload = z
  .object({ source_document_ids: z.array(UuidString).min(1).max(10_000) })
  .strict();
export type Tier3DocumentsBatchPayload = z.infer<typeof Tier3DocumentsBatchPayload>;

/** #4 — откат отправки. */
export const Tier3RollbackPayload = z
  .object({ push_id: UuidString, reason: z.string().min(1).max(2000) })
  .strict();
export type Tier3RollbackPayload = z.infer<typeof Tier3RollbackPayload>;

/** #5–#10 — один документ (ЭСФ, КПН, контрагент, счёт, платёж, взаиморасчёт). */
export const Tier3DocumentPayload = z.object({ id: UuidString }).strict();
export type Tier3DocumentPayload = z.infer<typeof Tier3DocumentPayload>;

/** #11 — отмена завершённого заказа. */
export const Tier3WorkOrderCancelPayload = z
  .object({ work_order_id: UuidString, reason: z.string().min(10).max(2000) })
  .strict();
export type Tier3WorkOrderCancelPayload = z.infer<typeof Tier3WorkOrderCancelPayload>;

/**
 * B67 (Ф6, SOLUTION §9.3 п. 2) — предмет заявки уровня 3. Одна строка матрицы может покрывать
 * несколько предметов (`documents_push_1c` — пакет документов, счёт, платёж, взаиморасчёт), и
 * путь исполнения зависит от предмета: пакеты и мастер-данные идут через воркер, документы —
 * синхронно через адаптер, заказ в 1С не ходит вовсе.
 */
export const Tier3SubjectKind = z.enum([
  'master_data',
  'onec_batch',
  'onec_push_event',
  'regulatory_doc',
  'counterparty',
  'invoice',
  'payment',
  'settlement',
  'work_order',
]);
export type Tier3SubjectKind = z.infer<typeof Tier3SubjectKind>;

/** B67 — почему действие уровня 3 сейчас недоступно (кнопка видна всегда, с причиной). */
export const Tier3AvailabilityReason = z.enum([
  'integration_not_connected',
  'no_eligible_cosigner',
  'initiator_not_in_zone',
  /**
   * С-I4 Р.3, круг 2 гейта (UI и UX, MAJOR) — второй подписант есть, но все такие — без имени в
   * профиле, а смотрящий их почту не видит: выбрать некого. Причина — у кнопки, до окна заявки.
   */
  'cosigner_unnamed',
]);
export type Tier3AvailabilityReason = z.infer<typeof Tier3AvailabilityReason>;

export const Tier3Availability = z
  .object({ available: z.boolean(), reason: Tier3AvailabilityReason.nullable() })
  .strict();
export type Tier3Availability = z.infer<typeof Tier3Availability>;

/** B71–B72 (Ф6, SOLUTION §9.4) — строки матрицы уровня 3, по которым создаются заявки. */
export const Tier3TaskType = z.enum([
  'master_data_push_1c',
  'aggregates_push_1c',
  'documents_push_1c',
  'regulatory_push_esf',
  'regulatory_push_kpn',
  'counterparty_push_1c',
  'onec_push_rollback',
  'work_order_cancel_completed',
]);
export type Tier3TaskType = z.infer<typeof Tier3TaskType>;

/** Подпись заявки уровня 3: кто, в какой роли и зоне, когда. */
export const Tier3SignatureRow = z
  .object({
    user_id: UuidString,
    name: z.string().nullable(),
    role: z.string(),
    zone: z.enum(['finance', 'management', 'production']),
    signed_at: IsoDateTime,
  })
  .strict();
export type Tier3SignatureRow = z.infer<typeof Tier3SignatureRow>;

export const Tier3Candidate = z
  .object({
    id: UuidString,
    /** С-I4 Р.3 (TCA-B13) — почта только смотрящему с правом администрирования пользователей. */
    email: z.string().nullable(),
    full_name: z.string().nullable(),
    role: z.string(),
    zone: z.enum(['finance', 'management', 'production']),
  })
  .strict();
export type Tier3Candidate = z.infer<typeof Tier3Candidate>;

/**
 * B72 — что уйдёт. Суммы — строками Decimal, валюта — кодом; подписи строк собирает фронт
 * (i18n-инвариант: сервер не шлёт freeform-текст).
 */
export const Tier3PreviewSummary = z
  .object({
    documents: z
      .array(
        z
          .object({
            id: UuidString,
            kind: Tier3SubjectKind,
            number: z.string().nullable(),
            state: z.string().nullable(),
          })
          .strict(),
      )
      .max(500),
    documents_total: z.number().int().nonnegative(),
    records: z.number().int().nonnegative(),
    amounts: z.array(MoneyAmount),
    period: z.string().nullable(),
    /** Крайний срок документа (ЭСФ — срок выписки); `null` — срока нет. */
    deadline: z.string().date().nullable(),
  })
  .strict();
export type Tier3PreviewSummary = z.infer<typeof Tier3PreviewSummary>;

export const Tier3PreviewQuery = z
  .object({
    task_type: Tier3TaskType,
    subject_kind: Tier3SubjectKind,
    /** Тело действия (`Tier3*Payload`) JSON-строкой. */
    payload: z.string().min(2).max(200_000),
  })
  .strict();
export type Tier3PreviewQuery = z.infer<typeof Tier3PreviewQuery>;

export const Tier3PreviewResponse = z
  .object({
    task_type: Tier3TaskType,
    subject_kind: Tier3SubjectKind,
    subject_id: z.string(),
    entity_id: UuidString,
    summary: Tier3PreviewSummary,
    availability: Tier3Availability,
    initiator_zone: z.enum(['finance', 'management', 'production']).nullable(),
    candidates: z.array(Tier3Candidate),
    /**
     * С-I4 Р.3 (TCA-B13, гейт круга 1) — подходящие вторые подписанты без имени в профиле, которых
     * смотрящему не показали: почту видит только администратор пользователей, а без почты строки
     * неразличимы. Фронт называет их числом и говорит, кто заполняет имя. Администратору — 0 (он
     * видит их в `candidates` с почтой).
     */
    candidates_unnamed: z.number().int().nonnegative(),
    /**
     * С-I3 Р.6 (`NC-137`) — зоны строки матрицы: нужны две подписи, по одной от каждой зоны.
     * `people` — кто в компании документа подписывает в этой зоне; заполняется только при
     * `initiator_not_in_zone`, чтобы причина «ваша роль не подписывает» назвала, к кому идти
     * (круг 1 гейта: люди — по зонам, а не общим списком по алфавиту).
     * С-I4 Р.3 — `unnamed`: сколько подписантов роли без имени в профиле скрыто (как у
     * `candidates_unnamed`).
     */
    signer_zones: z.array(
      z
        .object({
          zone: z.enum(['finance', 'management', 'production']),
          roles: z.array(z.string()),
          people: z.array(Tier3Candidate),
          unnamed: z.array(
            z.object({ role: z.string(), count: z.number().int().positive() }).strict(),
          ),
        })
        .strict(),
    ),
    /** Активная заявка на этот предмет, если уже есть. */
    active_request_id: UuidString.nullable(),
    /**
     * С-G Р.5 — код ошибки, по которому предмет нельзя отправить (документ отменён, пакет с
     * чужими документами…). Текст — `m.error_by_code(blocked_code)` на фронте; `null` — не
     * заблокирован.
     */
    blocked_code: z.string().nullable(),
  })
  .strict();
export type Tier3PreviewResponse = z.infer<typeof Tier3PreviewResponse>;

export const Tier3RequestCreateBody = z
  .object({
    task_type: Tier3TaskType,
    subject_kind: Tier3SubjectKind,
    payload: z.record(z.unknown()),
    addressee_user_id: UuidString,
    reason: z.string().max(2000).optional(),
  })
  .strict();
export type Tier3RequestCreateBody = z.infer<typeof Tier3RequestCreateBody>;

export const Tier3RequestCreateResponse = z
  .object({
    request_id: UuidString,
    status: z.literal('waiting'),
    signatures: z.array(Tier3SignatureRow),
    availability: Tier3Availability,
  })
  .strict();
export type Tier3RequestCreateResponse = z.infer<typeof Tier3RequestCreateResponse>;

/** B71 — переадресация и отзыв заявки уровня 3 (под `/approvals`). */
export const Tier3ReassignBody = z
  .object({ addressee_user_id: UuidString, reason: z.string().max(2000).optional() })
  .strict();
export type Tier3ReassignBody = z.infer<typeof Tier3ReassignBody>;
export const Tier3WithdrawBody = z.object({ reason: z.string().max(2000).optional() }).strict();
export type Tier3WithdrawBody = z.infer<typeof Tier3WithdrawBody>;

/** F91 (Ф6) — история отправок в 1С для не-админского экрана запуска (модуль финансов). */
export const Tier3OnecPushEventRow = z
  .object({
    id: UuidString,
    push_type: z.string(),
    task_type: z.string(),
    status: z.string(),
    source_count: z.number().int().nonnegative(),
    approval_request_id: UuidString.nullable(),
    pushed_at: IsoDateTime.nullable(),
    created_at: IsoDateTime,
    error_code: z.string().nullable(),
    rollback_push_id: UuidString.nullable(),
    rolled_back_at: IsoDateTime.nullable(),
  })
  .strict();
export type Tier3OnecPushEventRow = z.infer<typeof Tier3OnecPushEventRow>;
export const Tier3OnecPushHistoryResponse = z
  .object({ items: z.array(Tier3OnecPushEventRow) })
  .strict();
export type Tier3OnecPushHistoryResponse = z.infer<typeof Tier3OnecPushHistoryResponse>;

export const OnecMasterPushResponse = z
  .object({
    ok: z.literal(true),
    job_ids: z.array(z.string()).min(1),
  })
  .strict();
export type OnecMasterPushResponse = z.infer<typeof OnecMasterPushResponse>;

// ─── S10 Stock Entry submit-preview (dry-run) ──────────────────────────

export const SubmitPreviewSle = z
  .object({
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable(),
    qty_change: DecimalString,
    valuation_rate: MoneyAmount,
    balance_qty_after: DecimalString,
    balance_value_after: MoneyAmount,
    /** F36 (WHS-F36) — изменение стоимости строки: qty_change × valuation_rate. */
    value_change: MoneyAmount.optional(),
    item_name: z.string().nullable().optional(),
    item_sku: z.string().nullable().optional(),
    warehouse_name: z.string().nullable().optional(),
    warehouse_code: z.string().nullable().optional(),
    batch_no: z.string().nullable().optional(),
  })
  .strict();
export type SubmitPreviewSle = z.infer<typeof SubmitPreviewSle>;

export const SubmitPreviewResponse = z
  .object({
    stock_entry_id: UuidString,
    expected_sle: z.array(SubmitPreviewSle),
    negative_stock_violations: z.array(
      z
        .object({
          item_id: UuidString,
          warehouse_id: UuidString,
          batch_id: UuidString.nullable(),
          projected_qty: DecimalString,
        })
        .strict(),
    ),
    warnings: z.array(
      z
        .object({
          type: z.enum(['near_expiry_batch', 'low_stock_margin', 'bom_shortage', 'serial_missing']),
          severity: z.enum(['info', 'warn', 'critical']),
          message: z.string(),
          item_id: UuidString.optional(),
          batch_id: UuidString.optional(),
        })
        .strict(),
    ),
    generated_at: IsoDateTime,
  })
  .strict();
export type SubmitPreviewResponse = z.infer<typeof SubmitPreviewResponse>;

// ─── S10 Stock Reconciliation wizard (draft → preview → submit) ─────

export const StockReconciliationPreviewLine = z
  .object({
    item_id: UuidString,
    /**
     * B49 — поля, которые хендлер отдаёт с гейта C (раунды 6, 9), а контракт не
     * объявлял: строгая схема отбила бы собственный ответ сервера. Предмет решения
     * назван, а не закодирован.
     */
    item_label: z.string().nullable(),
    item_sku: z.string().nullable(),
    batch_no: z.string().nullable(),
    warehouse_id: UuidString,
    batch_id: UuidString.nullable(),
    expected_qty: DecimalString,
    counted_qty: DecimalString,
    variance_qty: DecimalString,
    variance_value: MoneyAmount,
    severity: z.enum(['match', 'within_tolerance', 'variance', 'critical_variance']),
    reason_required: z.boolean(),
    /** F53 (WHS-F21): строка черновика — адрес для ввода причины прямо в предпросмотре. */
    line_id: UuidString,
    /** F53 (WHS-F21): уже введённая причина отклонения (null — не указана). */
    variance_reason: z.string().nullable(),
  })
  .strict();
export type StockReconciliationPreviewLine = z.infer<typeof StockReconciliationPreviewLine>;

export const StockReconciliationPreviewResponse = z
  .object({
    reconciliation_id: UuidString,
    lines: z.array(StockReconciliationPreviewLine),
    summary: z
      .object({
        total_variance_value: MoneyAmount,
        items_affected: z.number().int().nonnegative(),
        requires_reasons: z.array(UuidString),
        /** B49 (WHS-F19): M — все позиции документа. */
        total: z.number().int().nonnegative(),
        /** B49 (WHS-F19): K — позиции с отклонением. */
        with_variance: z.number().int().nonnegative(),
      })
      .strict(),
    /** B49 (WHS-F22): метод оценки компании — источник денежных чисел. */
    valuation_method: ValuationMethod,
    /** B49 (WHS-F22): дата пересчёта — «ожидается» есть остаток STAITER на неё. */
    as_of: IsoDateTime,
    /**
     * B38 (В-23): проведение инвентаризации необратимо — отмена идёт компенсирующей
     * записью с согласующим, а не откатом. Флаг — для диалога предпросмотра (F53).
     */
    irreversible: z.literal(true),
    generated_at: IsoDateTime,
  })
  .strict();
export type StockReconciliationPreviewResponse = z.infer<typeof StockReconciliationPreviewResponse>;

// ─── S10 Landed Cost submit / cancel bodies ────────────────────────────

export const LandedCostSubmitBody = z
  .object({
    signature: ApprovalSignature,
    remarks: z.string().max(1000).optional(),
  })
  .strict();
export type LandedCostSubmitBody = z.infer<typeof LandedCostSubmitBody>;

export const LandedCostCancelBody = z
  .object({
    signature: ApprovalSignature,
    reason: z.string().min(1).max(2000),
  })
  .strict();
export type LandedCostCancelBody = z.infer<typeof LandedCostCancelBody>;

// ─── S11 Work Order contracts ──────────────────────────────────────────

/**
 * Вход для POST /api/v1/work-orders (create draft).
 * BOM должен быть активной версии — bom_template_key/version снепшотятся на WO.
 */
export const WoCreateInput = z.object({
  /**
   * W2 — опциональный. Если не задан (или 'AUTO'), backend генерит `WO-{YYYY}-{NNNNN}`
   * через Postgres sequence per entity per year. FE может опустить поле.
   */
  voucher_no: z.string().min(1).optional(),
  item_id: UuidString,
  bom_id: UuidString,
  qty_to_produce: PositiveDecimalString,
  /** UI-alias для `qty_to_produce`. */
  qty: PositiveDecimalString.optional(),
  source_warehouse_id: UuidString.nullable().optional(),
  wip_warehouse_id: UuidString.nullable().optional(),
  fg_warehouse_id: UuidString.nullable().optional(),
  scrap_warehouse_id: UuidString.nullable().optional(),
  planned_start: IsoDateTime.nullable().optional(),
  /** UI-alias для `planned_start`. */
  planned_start_at: IsoDateTime.nullable().optional(),
  planned_end: IsoDateTime.nullable().optional(),
  /** UI-alias для `planned_end`. */
  planned_end_at: IsoDateTime.nullable().optional(),
  /** Назначенный исполнитель (планировщик). */
  executor_id: UuidString.nullable().optional(),
  remarks: z.string().max(2000).optional(),
  /** UI-alias для remarks. */
  notes: z.string().max(2000).optional(),
  /** UI-only: номер ваучера как опциональное поле (если не задан — backend генерит). */
  voucher_no_optional: z.string().optional(),
});
export type WoCreateInput = z.infer<typeof WoCreateInput>;

/**
 * Body для POST /work-orders/:id/signal и parallel REST aliases.
 * release/start/pause-external/resume-external/complete не требуют подписи;
 * cancel (released/completed) требует подпись (Tier 2).
 */
export const WoSignalBody = z
  .object({
    signal: WoSignalType,
    reason: z.string().max(2000).optional(),
    signatures: z.array(ApprovalSignature).min(1).max(2).optional(),
    /** Для cancel после complete (Tier 3) — 2 подписи + compensating SE reason. */
    idempotency_key: z.string().max(200).optional(),
  })
  .strict();
export type WoSignalBody = z.infer<typeof WoSignalBody>;

/** Одна строка required material в preview. */
export const ManufacturePreviewRequired = z.object({
  item_id: UuidString,
  item_code: z.string().optional(),
  item_name: z.string().optional(),
  /** W10.3 — item.sku для отображения «{name} ({sku})». */
  item_sku: z.string().optional(),
  uom: z.string(),
  required_qty: DecimalString,
  /** UI-alias для required_qty. */
  qty_required: DecimalString.optional(),
  /** UI-alias — доступное количество на складе. */
  qty_available: DecimalString.optional(),
  /** UI-alias — дефицит (required - available). */
  shortage: DecimalString.optional(),
  from_batch_id: UuidString.nullable(),
  /** W10.3 — batch human-readable label. */
  batch_no: z.string().nullable().optional(),
  /** W10.3 — source warehouse resolved label. */
  from_warehouse_id: UuidString.nullable().optional(),
  from_warehouse_name: z.string().nullable().optional(),
  valuation_rate: MoneyAmount,
  depth: z.number().int().nonnegative(),
  is_alternate: z.boolean(),
});
export type ManufacturePreviewRequired = z.infer<typeof ManufacturePreviewRequired>;

/** Одна строка available stock в preview. */
export const ManufacturePreviewAvailable = z
  .object({
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable(),
    available_qty: DecimalString,
  })
  .strict();
export type ManufacturePreviewAvailable = z.infer<typeof ManufacturePreviewAvailable>;

/** Одна строка shortage в preview. */
export const ManufacturePreviewShortage = z
  .object({
    item_id: UuidString,
    required_qty: DecimalString,
    available_qty: DecimalString,
    shortage_qty: DecimalString,
  })
  .strict();
export type ManufacturePreviewShortage = z.infer<typeof ManufacturePreviewShortage>;

/**
 * W1.2 canonical WoWarning — structural-only (B-A.6).
 * Backend НЕ формирует `message` — FE композирует через `@staiter/i18n`:
 *   m.wo_warning_type_label({type}) + m.wo_warning_{type}_body(params).
 */
export const WoWarning = z
  .object({
    type: WoWarningType,
    severity: AnomalySeverity,
    related_item_id: UuidString.nullable().optional(),
    related_batch_id: UuidString.nullable().optional(),
    related_warehouse_id: UuidString.nullable().optional(),
    /** Shortage meta (insufficient_stock). Decimal-string, parsed на FE. */
    qty_needed: z.string().nullable().optional(),
    qty_available: z.string().nullable().optional(),
    /** UoM для formatQty на FE. */
    uom_code: z.string().nullable().optional(),
    /** Для bom_alternate_used: ссылка на рекомендованную альтернативу. */
    suggested_alternate_item_id: UuidString.nullable().optional(),
    /** bom_alternate_used / bom_scrap_exceeded. */
    bom_version: z.string().nullable().optional(),
    /** bom_scrap_exceeded. */
    scrap_percent: z.string().nullable().optional(),
    /** batch_expiry_* */
    expiry_date: z.string().nullable().optional(),
    /** cost_variance_high. */
    cost_variance_pct: z.string().nullable().optional(),
    /** extensibility для будущих типов — НЕ содержит готового user-facing текста. */
    params: z.record(z.unknown()).optional(),
  })
  .strict();
export type WoWarning = z.infer<typeof WoWarning>;

/** Стоимость драфт (material + labor + overhead). */
export const WoCostBreakdown = z
  .object({
    material: MoneyAmount,
    labor: MoneyAmount,
    overhead: MoneyAmount,
    total: MoneyAmount,
  })
  .strict();
export type WoCostBreakdown = z.infer<typeof WoCostBreakdown>;

/**
 * Response для GET /work-orders/:id/manufacture-preview.
 *
 * W17.1 — canonical имя поля `requirements` (required); `required` alias удалён.
 * W17.2 — `can_release` required boolean (не optional).
 * W17.3 — `warnings: z.array(WoWarning)` со strict типом (без unknown cast).
 */
export const ManufacturePreviewResponse = z.object({
  work_order_id: UuidString,
  requirements: z.array(ManufacturePreviewRequired),
  available: z.array(ManufacturePreviewAvailable),
  shortages: z.array(ManufacturePreviewShortage),
  output: z.array(
    z
      .object({
        item_id: UuidString,
        qty: DecimalString,
        batch_id: UuidString.nullable(),
        valuation_rate: MoneyAmount,
      })
      .strict(),
  ),
  total_cost: WoCostBreakdown,
  warnings: z.array(WoWarning),
  can_release: z.boolean(),
  /** Баланс материалов (summary for UI). */
  balance_summary: z.record(z.unknown()).optional(),
  generated_at: IsoDateTime,
});
export type ManufacturePreviewResponse = z.infer<typeof ManufacturePreviewResponse>;

/** Вход POST /work-orders/:id/job-cards. */
export const JobCardInput = z
  .object({
    operation_id: UuidString.nullable().optional(),
    operation_name: z.string().min(1).max(200),
    workstation: z.string().max(200).nullable().optional(),
    position: z.number().int().nonnegative().optional(),
    planned_qty: PositiveDecimalString,
    completed_qty: PositiveDecimalString.optional(),
    scrapped_qty: PositiveDecimalString.optional(),
    time_minutes_planned: PositiveDecimalString,
    time_minutes_actual: PositiveDecimalString.optional(),
    operator_user_id: UuidString.nullable().optional(),
    hour_rate: MoneyAmount.optional(),
  })
  .strict();
export type JobCardInput = z.infer<typeof JobCardInput>;

export const JobCardUpdateInput = JobCardInput.partial().strict();
export type JobCardUpdateInput = z.infer<typeof JobCardUpdateInput>;

/** Bulk action WO. */
export const WoBulkActionInput = z
  .object({
    action: z.enum(['release', 'cancel', 'reassign']),
    ids: z.array(UuidString).min(1).max(500),
    reason: z.string().max(2000).optional(),
    new_executor_id: UuidString.nullable().optional(),
    signatures: z.array(ApprovalSignature).min(1).max(2).optional(),
  })
  .strict()
  .refine((v) => v.action !== 'reassign' || !!v.new_executor_id, {
    message: 'reassign requires new_executor_id',
    path: ['new_executor_id'],
  });
export type WoBulkActionInput = z.infer<typeof WoBulkActionInput>;

/** State timeline entry (stored + returned in detail). */
export const WoStateTimelineEntry = z.object({
  id: UuidString.optional(),
  work_order_id: UuidString,
  from_state: z.string().nullable(),
  to_state: z.string(),
  changed_by: UuidString.nullable(),
  changed_by_name: z.string().nullable().optional(),
  /**
   * F104 пака `tails-closeout` (NC-55) — роль инициатора на момент перехода, кодом роли из записи
   * журнала (`audit_events.actor_role`) того сигнала, что породил переход. Без неё строка истории
   * «Отменён · Айгуль Ким» не отвечала, по праву ли человек отменил. `null` — переход без сигнала
   * (системный, например автозакрытие) или сигнал старше журнала.
   */
  changed_by_role: z.string().nullable().optional(),
  changed_at: IsoDateTime,
  reason: z.string().nullable(),
  payload: z.record(z.unknown()).nullable().optional(),
  sle_ids: z.array(UuidString).optional(),
  /**
   * T2A-F8 — кто ПОДПИСАЛ переход, а не только кто его инициировал.
   *
   * Подписи Tier 2/3 существовали в журнале (`audit_events.approvals`) и не
   * доходили до экрана: таймлайн показывал `changed_by` — актора, то есть того,
   * кого согласование как раз и не должно было хватать. Роль едет кодом, подпись
   * — временем: имена и слова роли собирает фронт.
   */
  signers: z
    .array(
      z
        .object({
          user_id: UuidString,
          name: z.string().nullable(),
          role: z.string(),
          signed_at: IsoDateTime,
          /**
           * B81 пака `tails-closeout` (WO-B1) — запись журнала с этой подписью: экран открывает
           * её саму (`highlight`), а не вкладку «Аудит» целиком.
           */
          audit_event_id: UuidString.nullable(),
        })
        .strict(),
    )
    .optional(),
});
export type WoStateTimelineEntry = z.infer<typeof WoStateTimelineEntry>;

/** Detail response for GET /work-orders/:id. */
export const WoDetailResponse = z
  .object({
    header: z.record(z.unknown()),
    required_materials: z.array(ManufacturePreviewRequired),
    job_cards: z.array(z.record(z.unknown())),
    allocated: z.array(
      z
        .object({
          item_id: UuidString,
          allocated_qty: DecimalString,
        })
        .strict(),
    ),
    variance: z
      .object({
        planned_cost: WoCostBreakdown.nullable(),
        actual_cost: WoCostBreakdown.nullable(),
        variance_amount: MoneyAmount.nullable(),
        variance_pct: z.number().nullable(),
        reason: z.string().nullable(),
      })
      .strict(),
    state_timeline: z.array(WoStateTimelineEntry),
    warnings: z.array(WoWarning),
  })
  .strict();
export type WoDetailResponse = z.infer<typeof WoDetailResponse>;

export const AuditListQuery = z
  .object({
    resource_type: z.string().optional(),
    resource_id: z.string().optional(),
    actor_user_id: UuidString.optional(),
    action: z.string().optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    format: z.enum(['json', 'csv', 'xlsx']).optional(),
  })
  .strict();
export type AuditListQuery = z.infer<typeof AuditListQuery>;

// ─── S17 Finance Dashboard ──────────────────────────────────────────
//
// Single-call KPI snapshot + time-series + top-counterparties для executive dashboard.
// Cross-sync с BACKLOG-2026-04-25-finance-dashboard-frontend.md §6 — любое изменение
// здесь требует синхронизирующего PR в FE-бэклог.

const DashboardCurrency = z.enum(['KZT', 'USD', 'EUR', 'RUB']);
export type DashboardCurrency = z.infer<typeof DashboardCurrency>;

const DashboardSeriesMetric = z.enum([
  'revenue',
  'cogs',
  'opex',
  'gross_profit',
  'net_profit',
  'cash_inflow',
  'cash_outflow',
  'cash_balance',
  'ar_outstanding',
  'ap_outstanding',
]);
export type DashboardSeriesMetric = z.infer<typeof DashboardSeriesMetric>;

const TopCounterpartiesMetric = z.enum(['revenue', 'ar_balance', 'ap_balance', 'gross_margin']);
export type TopCounterpartiesMetric = z.infer<typeof TopCounterpartiesMetric>;

const DashboardGranularity = z.enum(['day', 'week', 'month']);
export type DashboardGranularity = z.infer<typeof DashboardGranularity>;

// PnlBasis already imported at top; re-export для public API.
export { PnlBasis } from './entities/pnl.js';

/** Финансовый дашборд: KPI агрегаты. */
export const FinanceDashboardKpi = z
  .object({
    schema_version: z.literal('1.0.0'),
    revenue: MoneyAmount,
    revenue_delta_pct: DecimalString.nullable(),
    gross_profit: MoneyAmount,
    gross_margin_pct: DecimalString.nullable(),
    net_profit: MoneyAmount,
    net_margin_pct: DecimalString.nullable(),
    operating_cash_flow: MoneyAmount,
    operating_cash_flow_delta_pct: DecimalString.nullable(),
    cash_position: MoneyAmount,
    ar_outstanding: MoneyAmount,
    ar_aging: z
      .object({
        bucket_0_30: MoneyAmount,
        bucket_31_60: MoneyAmount,
        bucket_61_90: MoneyAmount,
        bucket_90_plus: MoneyAmount,
      })
      .strict(),
    ap_outstanding: MoneyAmount,
    budget_variance_pct: DecimalString.nullable(),
  })
  .strict();
export type FinanceDashboardKpi = z.infer<typeof FinanceDashboardKpi>;

export const DashboardQuerySchema = z
  .object({
    date_from: IsoDate,
    date_to: IsoDate,
    cost_center: z.string().min(1).max(64).optional(),
    project_code: z.string().min(1).max(64).optional(),
    currency: DashboardCurrency.default('KZT'),
    counterparty_group: z.array(UuidString).max(50).optional(),
    compare_to_previous_period: QueryBool.default(false),
    budget_id: UuidString.optional(),
    pnl_basis: PnlBasis.optional(),
  })
  .strict()
  .refine((q) => new Date(q.date_to) >= new Date(q.date_from), {
    message: 'date_to_before_from',
    path: ['date_to'],
  });
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

export const DashboardKpiResponseSchema = z
  .object({
    schema_version: z.literal('1.0.0'),
    period: z.object({ date_from: IsoDate, date_to: IsoDate }).strict(),
    comparison_period: z.object({ date_from: IsoDate, date_to: IsoDate }).strict().nullable(),
    currency: DashboardCurrency,
    fx_rate_as_of: IsoDate.nullable(),
    cash_position_is_approximation: z.boolean(),
    pnl_basis: PnlBasis,
    kpi: FinanceDashboardKpi,
    generated_at: IsoDateTime,
  })
  .strict();
export type DashboardKpiResponse = z.infer<typeof DashboardKpiResponseSchema>;

export const DashboardSeriesQuerySchema = z
  .object({
    date_from: IsoDate,
    date_to: IsoDate,
    granularity: DashboardGranularity.default('month'),
    metrics: z.array(DashboardSeriesMetric).min(1).max(10),
    cost_center: z.string().min(1).max(64).optional(),
    project_code: z.string().min(1).max(64).optional(),
    currency: DashboardCurrency.default('KZT'),
    pnl_basis: PnlBasis.optional(),
  })
  .strict()
  .refine((q) => new Date(q.date_to) >= new Date(q.date_from), {
    message: 'date_to_before_from',
    path: ['date_to'],
  });
export type DashboardSeriesQuery = z.infer<typeof DashboardSeriesQuerySchema>;

export const DashboardSeriesBucket = z
  .object({
    bucket_start: IsoDate,
    bucket_end: IsoDate,
    values: z.record(DashboardSeriesMetric, MoneyAmount),
  })
  .strict();
export type DashboardSeriesBucket = z.infer<typeof DashboardSeriesBucket>;

export const DashboardSeriesResponseSchema = z
  .object({
    schema_version: z.literal('1.0.0'),
    granularity: DashboardGranularity,
    currency: DashboardCurrency,
    pnl_basis: PnlBasis,
    buckets: z.array(DashboardSeriesBucket),
    generated_at: IsoDateTime,
  })
  .strict();
export type DashboardSeriesResponse = z.infer<typeof DashboardSeriesResponseSchema>;

export const TopCounterpartiesQuerySchema = z
  .object({
    date_from: IsoDate,
    date_to: IsoDate,
    metric: TopCounterpartiesMetric,
    limit: z.coerce.number().int().min(1).max(50).default(10),
    direction: z.enum(['desc', 'asc']).default('desc'),
    currency: DashboardCurrency.default('KZT'),
    cost_center: z.string().min(1).max(64).optional(),
    project_code: z.string().min(1).max(64).optional(),
    counterparty_group: z.array(UuidString).max(50).optional(),
  })
  .strict()
  .refine((q) => new Date(q.date_to) >= new Date(q.date_from), {
    message: 'date_to_before_from',
    path: ['date_to'],
  });
export type TopCounterpartiesQuery = z.infer<typeof TopCounterpartiesQuerySchema>;

export const TopCounterpartiesResponseSchema = z
  .object({
    schema_version: z.literal('1.0.0'),
    metric: TopCounterpartiesMetric,
    currency: DashboardCurrency,
    rows: z
      .array(
        z
          .object({
            counterparty_id: UuidString,
            name: z.string().min(1).max(255),
            bin_iin: z.string().min(1).max(20).nullable(),
            value: MoneyAmount,
            share_pct: DecimalString,
          })
          .strict(),
      )
      .max(50),
    totals: z
      .object({
        value: MoneyAmount,
        coverage_pct: DecimalString,
      })
      .strict(),
    generated_at: IsoDateTime,
  })
  .strict();
export type TopCounterpartiesResponse = z.infer<typeof TopCounterpartiesResponseSchema>;

// ─── S-FIX-5 — List endpoint schemas с denormalized counterparty_name ──
// _Invoice/_Payment/_Settlement imported at top (line 23-25). Дубликат в
// середине файла создавал TDZ ошибку при runtime: `Cannot access 'RunSummary'
// before initialization` (инцидент v0.3.2 deploy). Imports должны быть наверху.

/**
 * S-FIX-5-BE.1.1 + 8.1 — InvoiceListItem с denormalized counterparty_name_ru/en.
 * `lines` opt out (heavy в листинге; detail endpoint возвращает full Invoice).
 * counterparty_name_ru/en — nullable (если counterparty soft-deleted или ещё не synced).
 * Optional на уровне Zod-схемы — для backward-compat со старыми BE responses.
 */
export const InvoiceListItem = _Invoice.omit({ lines: true }).extend({
  counterparty_name_ru: z.string().nullable().optional(),
  counterparty_name_en: z.string().nullable().optional(),
});
export type InvoiceListItem = z.infer<typeof InvoiceListItem>;

export const InvoiceListResponse = z
  .object({
    schema_version: z.literal('1.0.0').optional(),
    items: z.array(InvoiceListItem),
    total_count: z.number().int().nonnegative().optional(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
    next_cursor: z.string().nullable().optional(),
    // access-control (F9) — denorm имён компаний (holding-агрегат).
    entities: z.record(z.object({ name: z.string() }).passthrough()).optional(),
  })
  .strict();
export type InvoiceListResponse = z.infer<typeof InvoiceListResponse>;

/** S-FIX-5-BE.1.2 + 8.1 — PaymentListItem с denormalized counterparty_name. */
export const PaymentListItem = _Payment.extend({
  counterparty_name_ru: z.string().nullable().optional(),
  counterparty_name_en: z.string().nullable().optional(),
});
export type PaymentListItem = z.infer<typeof PaymentListItem>;

export const PaymentListResponse = z
  .object({
    schema_version: z.literal('1.0.0').optional(),
    items: z.array(PaymentListItem),
    // S-FIX-6 fix: BE list endpoints возвращают next_cursor (см. /payments,
    // /settlements handlers + MSW handlers). Без accepting его strict schema
    // отклоняла валидный response → тест phase2-msw fail.
    next_cursor: z.string().nullable().optional(),
    total_count: z.number().int().nonnegative().optional(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
    // access-control (F9) — denorm имён компаний (holding-агрегат).
    entities: z.record(z.object({ name: z.string() }).passthrough()).optional(),
  })
  .strict();
export type PaymentListResponse = z.infer<typeof PaymentListResponse>;

/**
 * S-FIX-5-BE.1.3 + 8.1 — SettlementListItem с counterparty_name (primary counterparty).
 * Settlement.lines может содержать N counterparty — здесь primary (first line.counterparty).
 */
export const SettlementListItem = _Settlement.omit({ lines: true }).extend({
  counterparty_id: UuidString.nullable().optional(),
  counterparty_name_ru: z.string().nullable().optional(),
  counterparty_name_en: z.string().nullable().optional(),
});
export type SettlementListItem = z.infer<typeof SettlementListItem>;

export const SettlementListResponse = z
  .object({
    schema_version: z.literal('1.0.0').optional(),
    items: z.array(SettlementListItem),
    // S-FIX-6 fix: см. PaymentListResponse — strict + next_cursor.
    next_cursor: z.string().nullable().optional(),
    total_count: z.number().int().nonnegative().optional(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
    // access-control (F9) — denorm имён компаний (holding-агрегат).
    entities: z.record(z.object({ name: z.string() }).passthrough()).optional(),
  })
  .strict();
export type SettlementListResponse = z.infer<typeof SettlementListResponse>;

// ─── S-FIX-5-BE.4.6 + 8.3 — UsageCheckResponse для destructive pre-check ─

/**
 * S-FIX-5-BE.4.6 — Single reference type bundle.
 *
 * `type` — semantic enum (FE i18n-keys ему сопоставит):
 *   'invoice' | 'payment' | 'settlement' | 'cost_allocation' | 'sle' |
 *   'stock_balance' | 'bom_line' | 'wo_active' | 'child_warehouse'.
 *
 * `sample` — до 5 reference rows для preview в ConfirmDialog.
 */
export const UsageReference = z
  .object({
    type: z.string().min(1),
    count: z.number().int().nonnegative(),
    sample: z
      .array(
        z
          .object({
            id: UuidString,
            label: z.string(),
          })
          .strict(),
      )
      .max(5),
  })
  .strict();
export type UsageReference = z.infer<typeof UsageReference>;

/** Generic UsageCheckResponse — returned by GET /:resource/:id/usage. */
export const UsageCheckResponse = z
  .object({
    schema_version: z.literal('1.0.0'),
    can_delete: z.boolean(),
    references: z.array(UsageReference),
  })
  .strict();
export type UsageCheckResponse = z.infer<typeof UsageCheckResponse>;

// ─── S-FIX-5-BE.8.5 — ApiErrorCode enum extension for auth.* ─────────

/**
 * S-FIX-5-BE.7.1 — auth.* error codes.
 *
 * Это semantic enum; реальный ErrorCode enum extends его.
 * FE error-renderer.ts добавляет case'ы.
 */
export const AuthErrorCode = z.enum([
  'auth.unauthorized',
  'auth.forbidden',
  'auth.invalid_credentials',
  'auth.password_change_required',
]);
export type AuthErrorCode = z.infer<typeof AuthErrorCode>;

// ─── EBE-6 — Update endpoints (PATCH) request/response schemas ────────
//
// SSOT for cross-entity PATCH endpoints. FE и BE импортят те же types.
// Каждый schema:
//   - все поля optional (partial update);
//   - `expected_updated_at` для optimistic concurrency (EBE-7);
//   - `.strict()` per CLAUDE.md;
//   - response — full re-fetched entity (use existing detail schema FE-side).
// Tier 2 — AuditEvent emit обязателен на BE (EBE-8).
// State guard: `assertEditable(entity, {editableStates: ['draft']})` (EBE-9).

const MoneyAmountInput = z
  .object({
    // Инвариант MoneyAmount (S12.2) — сумма через примитив DecimalString
    // (не bare z.string().regex): input-форма MoneyAmount с жёстким enum валют.
    amount: DecimalString,
    currency: z.enum(['KZT', 'USD', 'EUR', 'RUB', 'CNY']),
  })
  .strict();

/** EBE-1 — InvoiceUpdateRequest (PATCH /api/v1/invoices/:id). */
export const InvoiceLineUpdateInput = z
  .object({
    line_no: z.number().int().min(1),
    item_id: UuidString.nullable().optional(),
    description_ru: z.string().min(1),
    description_kk: z.string().nullable().optional(),
    qty: z.string().regex(/^\d+(\.\d+)?$/),
    uom_code: z.string(),
    unit_price: MoneyAmountInput,
    line_subtotal: MoneyAmountInput,
    vat_rate: z.enum(['0', '12', 'exempt']),
    vat_amount: MoneyAmountInput,
    line_total: MoneyAmountInput,
  })
  .strict();
export type InvoiceLineUpdateInput = z.infer<typeof InvoiceLineUpdateInput>;

export const InvoiceUpdateRequest = z
  .object({
    issue_date: IsoDate.optional(),
    due_date: IsoDate.optional(),
    counterparty_id: UuidString.optional(),
    notes_ru: z.string().nullable().optional(),
    // Full-replace semantics: если lines присутствует — заменяет все строки.
    lines: z.array(InvoiceLineUpdateInput).min(1).optional(),
    expected_updated_at: IsoDateTime.optional(),
  })
  .strict();
export type InvoiceUpdateRequest = z.infer<typeof InvoiceUpdateRequest>;

/** EBE-2 — PaymentUpdateRequest (PATCH /api/v1/payments/:id). */
export const PaymentUpdateRequest = z
  .object({
    payment_date: IsoDate.optional(),
    amount: MoneyAmountInput.optional(),
    counterparty_id: UuidString.optional(),
    method: z.enum(['bank_transfer', 'cash', 'card', 'offset', 'other']).optional(),
    direction: z.enum(['inflow', 'outflow']).optional(),
    bank_account_ref: z.string().nullable().optional(),
    external_ref: z.string().nullable().optional(),
    notes_ru: z.string().nullable().optional(),
    expected_updated_at: IsoDateTime.optional(),
  })
  .strict();
export type PaymentUpdateRequest = z.infer<typeof PaymentUpdateRequest>;

/** EBE-3 — StockEntryUpdateRequest (PATCH /api/v1/stock-entries/:id, draft only). */
export const StockEntryLineUpdateInput = z
  .object({
    line_no: z.number().int().min(1),
    item_id: UuidString,
    qty: DecimalString,
    uom_code: z.string(),
    source_warehouse_id: UuidString.nullable().optional(),
    target_warehouse_id: UuidString.nullable().optional(),
    batch_id: UuidString.nullable().optional(),
    basic_rate: DecimalString.optional(),
    notes_ru: z.string().nullable().optional(),
  })
  .strict();
export type StockEntryLineUpdateInput = z.infer<typeof StockEntryLineUpdateInput>;

export const StockEntryUpdateRequest = z
  .object({
    posting_date: IsoDate.optional(),
    purpose: z.enum(['receipt', 'issue', 'transfer', 'manufacture', 'repack']).optional(),
    source_warehouse_id: UuidString.nullable().optional(),
    target_warehouse_id: UuidString.nullable().optional(),
    notes_ru: z.string().nullable().optional(),
    lines: z.array(StockEntryLineUpdateInput).min(1).optional(),
    expected_updated_at: IsoDateTime.optional(),
  })
  .strict();
export type StockEntryUpdateRequest = z.infer<typeof StockEntryUpdateRequest>;

/** EBE-4 — StockReconciliationUpdateRequest (PATCH /api/v1/stock-reconciliation/:id, draft only). */
export const StockReconciliationLineUpdateInput = z
  .object({
    line_no: z.number().int().min(1),
    item_id: UuidString,
    warehouse_id: UuidString,
    batch_id: UuidString.nullable().optional(),
    counted_qty: DecimalString,
    uom_code: z.string(),
    notes_ru: z.string().nullable().optional(),
  })
  .strict();
export type StockReconciliationLineUpdateInput = z.infer<typeof StockReconciliationLineUpdateInput>;

/**
 * F53 (WHS-F21) — причины отклонений строк черновика. Узкая запись: меняет ТОЛЬКО
 * `variance_reason`, строки и количества не пересобирает (общий PATCH строк их
 * перезаписывает с ожидаемым 0 — для ввода причин он разрушителен).
 */
export const StockReconciliationReasonsInput = z
  .object({
    lines: z
      .array(
        z
          .object({
            line_id: UuidString,
            variance_reason: VarianceReason.nullable(),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();
export type StockReconciliationReasonsInput = z.infer<typeof StockReconciliationReasonsInput>;

export const StockReconciliationUpdateRequest = z
  .object({
    posting_date: IsoDate.optional(),
    notes_ru: z.string().nullable().optional(),
    lines: z.array(StockReconciliationLineUpdateInput).min(1).optional(),
    expected_updated_at: IsoDateTime.optional(),
  })
  .strict();
export type StockReconciliationUpdateRequest = z.infer<typeof StockReconciliationUpdateRequest>;

/** EBE-5 — BudgetUpdateRequest (PATCH /api/v1/budgets/:id, draft only). */
export const BudgetUpdateRequest = z
  .object({
    name_ru: z.string().min(1).optional(),
    name_en: z.string().nullable().optional(),
    period_start: IsoDate.optional(),
    period_end: IsoDate.optional(),
    cost_center_id: UuidString.nullable().optional(),
    notes_ru: z.string().nullable().optional(),
    expected_updated_at: IsoDateTime.optional(),
  })
  .strict();
export type BudgetUpdateRequest = z.infer<typeof BudgetUpdateRequest>;

/** EBE-5 — BudgetLineUpdateRequest (POST/PATCH /api/v1/budgets/:id/lines[/:lineId]). */
export const BudgetLineUpdateRequest = z
  .object({
    account_code: z.string().min(1).optional(),
    period: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .optional(),
    plan_amount: MoneyAmountInput.optional(),
    cost_center_id: UuidString.nullable().optional(),
    notes_ru: z.string().nullable().optional(),
  })
  .strict();
export type BudgetLineUpdateRequest = z.infer<typeof BudgetLineUpdateRequest>;

/** EBE-5 — BudgetLineCreateRequest (POST /api/v1/budgets/:id/lines). */
export const BudgetLineCreateRequest = z
  .object({
    account_code: z.string().min(1),
    period: z.string().regex(/^\d{4}-\d{2}$/),
    plan_amount: MoneyAmountInput,
    cost_center_id: UuidString.nullable().optional(),
    notes_ru: z.string().nullable().optional(),
  })
  .strict();
export type BudgetLineCreateRequest = z.infer<typeof BudgetLineCreateRequest>;

/**
 * EBE-9 — Generic update response envelope.
 * Detail endpoints возвращают свою shape — этот тип просто документирует
 * минимум {id, state, updated_at} который FE может использовать для cache invalidation.
 */
export const EntityUpdateAck = z
  .object({
    id: UuidString,
    state: z.string().optional(),
    updated_at: IsoDateTime.optional(),
  })
  .strict();
export type EntityUpdateAck = z.infer<typeof EntityUpdateAck>;

/**
 * BE-B-1 — Person-card (лёгкая карточка пользователя для @упоминаний).
 * Минимум данных (не полный профиль): id, отображаемое имя, email, роль в
 * активной компании, активность. role — enum code, локализация на FE.
 */
export const UserCard = z
  .object({
    id: UuidString,
    full_name: z.string().nullable(),
    email: z.string().email(),
    role: z.string(),
    is_active: z.boolean(),
  })
  .strict();
export type UserCard = z.infer<typeof UserCard>;
